import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  IndianRupee, TrendingUp, TrendingDown, Percent, Receipt, CreditCard,
  Send, ExternalLink, Plus, Trash2, Calendar, Banknote, CheckCircle2,
  MessageCircle, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/utils';
import SendPaymentLinkDialog from '@/components/TripFinancials/SendPaymentLinkDialog';
import { LivePaymentLinksCard } from '@/components/TripFinancials/LivePaymentLinksCard';
import TaxDepositCard from '@/components/TripFinancials/TaxDepositCard';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = { pending: 'bg-amber-100 text-amber-700', paid: 'bg-emerald-100 text-emerald-700', partial: 'bg-blue-100 text-blue-700', overdue: 'bg-rose-100 text-rose-700', due: 'bg-slate-100 text-slate-600' };

const PAYMENT_METHODS = [
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
];

const EMPTY_FORM = {
  amount: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '+91',
  description: '',
  gateway: 'cashfree',
  expiresInHours: 72,
};

const TripFinancials = ({ tripId, tripRevenue, trip }) => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [waSendOpen, setWaSendOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Local mirror of trip-level schedule + receipts. Initialised from the
  // `trip` prop and kept in sync via the dedicated endpoints below.
  const [schedule, setSchedule] = useState(() => trip?.paymentSchedule || []);
  const [receipts, setReceipts] = useState(() => trip?.receipts || []);
  const [scheduleDraft, setScheduleDraft] = useState(() => trip?.paymentSchedule || []);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    foreignAmount: '',
    fxRate: '',
    method: 'bank_transfer',
    bankRef: '',
    note: '',
    accountId: '',
  });
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/accounting/bank-accounts`, { withCredentials: true })
      .then((r) => setBankAccounts(r.data || []))
      .catch(() => {});
  }, []);

  // Keep local state in sync when parent re-renders with a fresher trip.
  useEffect(() => {
    setSchedule(trip?.paymentSchedule || []);
    setScheduleDraft(trip?.paymentSchedule || []);
    setReceipts(trip?.receipts || []);
  }, [trip?.paymentSchedule, trip?.receipts]);

  const loadPaymentLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payments/links?limit=50`, { withCredentials: true });
      const tripLinks = (res.data?.items || []).filter((l) => l.tripId === tripId);
      setPaymentLinks(tripLinks);
    } catch (err) { console.debug('Payment links load skipped:', err?.message || err); }
  }, [tripId]);

  useEffect(() => {
    const load = async () => {
      try {
        const [expRes, payRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/accounting/expenses?trip_id=${tripId}`).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/payments/transactions?trip_id=${tripId}`).catch(() => ({ data: [] })),
        ]);
        setExpenses(Array.isArray(expRes.data) ? expRes.data : []);
        setPayments(Array.isArray(payRes.data) ? payRes.data : []);
        await loadPaymentLinks();
      } catch (err) {
        toast.error('Failed to load financial data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tripId, loadPaymentLinks]);

  // Auto-poll pending links every 15s — flips to "paid" when Cashfree/Razorpay reports success
  useEffect(() => {
    const pending = paymentLinks.filter((l) => l.status === 'pending');
    if (pending.length === 0) return;
    const interval = setInterval(async () => {
      let flipped = false;
      for (const l of pending) {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/payments/links/public/${l.token}/status`);
          if (r.data?.status === 'paid') flipped = true;
        } catch (err) { console.debug('Status poll transient error:', err?.message || err); }
      }
      if (flipped) {
        toast.success('Payment received!');
        loadPaymentLinks();
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [paymentLinks, loadPaymentLinks]);

  // ---- Derived metrics ----
  const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const totalGSTPaid = expenses.reduce((s, e) => s + (e.gstAmount || 0), 0);
  const totalTCSPaid = expenses.reduce((s, e) => s + (e.tcsAmount || 0), 0);
  const totalExpenseWithTax = expenses.reduce((s, e) => s + (e.totalAmount || e.amount || 0), 0);
  const profit = (tripRevenue || 0) - totalExpenseWithTax;
  const margin = tripRevenue > 0 ? ((profit / tripRevenue) * 100).toFixed(1) : 0;
  const paidPayments = payments.filter((p) => p.paymentStatus === 'paid');
  const totalGatewayCollected = paidPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const totalManualReceipts = useMemo(
    () => receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [receipts],
  );
  const totalCollected = totalGatewayCollected + totalManualReceipts;
  const outstanding = Math.max(0, (tripRevenue || 0) - totalCollected);

  // BATCH 2C — FX-mode (foreign-currency quote) awareness. Outstanding is
  // tracked in the FIXED foreign currency; INR is realised per-payment.
  const isFx = (trip?.quoteCurrencyMode || 'INR').toUpperCase() === 'FX';
  const fxCur = (trip?.quoteCurrency || 'USD').toUpperCase();
  const fxPayable = Number(trip?.fxClientAmount || 0);
  const fxReceived = useMemo(
    () => receipts.reduce((s, r) => s + (Number(r.foreignAmount) || 0), 0),
    [receipts],
  );
  const fxOutstanding = Math.max(0, fxPayable - fxReceived);
  const [fxMarkup, setFxMarkup] = useState(1);
  useEffect(() => {
    if (!isFx) return;
    axios.get(`${BACKEND_URL}/api/fx/rates`, { withCredentials: true })
      .then((r) => setFxMarkup(Number(r.data?.markupPerUnitInr) || 1))
      .catch(() => {});
  }, [isFx]);
  const fxPreviewInr = isFx
    ? (Number(receiptForm.foreignAmount) || 0) * ((Number(receiptForm.fxRate) || 0) + fxMarkup)
    : 0;

  // ---- Payment schedule editing ----
  const addScheduleRow = () => {
    setScheduleDraft((rows) => ([
      ...rows,
      { label: `Instalment ${rows.length + 1}`, amount: 0, dueDate: '', status: 'due' },
    ]));
  };
  const updateScheduleRow = (idx, key, val) => {
    setScheduleDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };
  const removeScheduleRow = (idx) => {
    setScheduleDraft((rows) => rows.filter((_, i) => i !== idx));
  };
  const persistSchedule = async () => {
    const cleaned = scheduleDraft
      .filter((r) => r.dueDate)
      .map((r) => ({
        label: r.label || 'Instalment',
        amount: Number(r.amount) || 0,
        dueDate: r.dueDate,
        status: r.status || 'due',
      }));
    setSavingSchedule(true);
    try {
      await axios.put(`${BACKEND_URL}/api/trips/${tripId}/payment-schedule`, cleaned, { withCredentials: true });
      setSchedule(cleaned);
      toast.success('Payment schedule saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save schedule'));
    } finally {
      setSavingSchedule(false);
    }
  };
  const scheduleDirty = useMemo(
    () => JSON.stringify(schedule) !== JSON.stringify(scheduleDraft),
    [schedule, scheduleDraft],
  );

  // ---- Manual receipt ----
  const addReceipt = async () => {
    if (!receiptForm.date) { toast.error('Pick a receipt date'); return; }
    let payload;
    if (isFx) {
      const fa = Number(receiptForm.foreignAmount);
      const rate = Number(receiptForm.fxRate);
      if (!fa || fa <= 0) { toast.error(`Enter the amount received in ${fxCur}`); return; }
      if (!rate || rate <= 0) { toast.error("Enter that day's FX rate"); return; }
      payload = {
        date: receiptForm.date,
        foreignAmount: fa,
        fxRate: rate,
        foreignCurrency: fxCur,
        method: receiptForm.method,
        bankRef: receiptForm.bankRef,
        note: receiptForm.note,
        accountId: receiptForm.accountId || null,
      };
    } else {
      const amt = Number(receiptForm.amount);
      if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
      // Fix (2026-09-05): only send the fields this endpoint expects for a
      // non-FX receipt — do NOT spread receiptForm as-is, it always carries
      // foreignAmount/fxRate as "" here, which 422s the backend.
      payload = {
        date: receiptForm.date,
        amount: amt,
        method: receiptForm.method,
        bankRef: receiptForm.bankRef,
        note: receiptForm.note,
        accountId: receiptForm.accountId || null,
      };
    }
    setSavingReceipt(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/trips/${tripId}/receipts`, payload, { withCredentials: true });
      setReceipts((rs) => [...rs, res.data]);
      setReceiptForm({
        date: new Date().toISOString().slice(0, 10),
        amount: '',
        foreignAmount: '',
        fxRate: '',
        method: 'bank_transfer',
        bankRef: '',
        note: '',
        accountId: '',
      });
      toast.success('Receipt recorded');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to record receipt'));
    } finally {
      setSavingReceipt(false);
    }
  };
  const deleteReceipt = async (receiptId) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/trips/${tripId}/receipts/${receiptId}`, { withCredentials: true });
      setReceipts((rs) => rs.filter((r) => r.receiptId !== receiptId));
      toast.success('Receipt removed');
    } catch (err) {
      toast.error('Failed to delete receipt');
    }
  };

  // ---- Send link handlers ----
  const openSendDialog = () => {
    setForm({
      amount: outstanding > 0 ? String(outstanding) : String(tripRevenue || ''),
      customerName: trip?.guestName || '',
      customerEmail: trip?.email || '',
      customerPhone: trip?.phone || '+91',
      description: `${trip?.destination || 'Travcations'} — ${trip?.tripId || tripId}`,
      gateway: 'cashfree',
      expiresInHours: 72,
    });
    setCreated(null);
    setShowSend(true);
  };

  const handleCreate = async () => {
    if (!form.amount || !form.customerName || !form.customerEmail || !form.customerPhone) {
      toast.error('Fill required fields');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/links`, {
        ...form,
        amount: parseFloat(form.amount),
        expiresInHours: parseInt(form.expiresInHours, 10) || 72,
        tripId,
      }, { withCredentials: true });
      setCreated(res.data);
      toast.success('Payment link ready to share');
      loadPaymentLinks();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to create payment link'));
    } finally {
      setCreating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!created?.linkId) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/links/${created.linkId}/send`, { via: 'email' }, { withCredentials: true });
      toast.success(res.data.message || 'Email sent');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send email'));
    }
  };

  const copyShareUrl = () => {
    const url = created?.short_url || created?.shareUrl
      || `${window.location.origin}/pay/${created?.payment_ref || created?.token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  // ---- WhatsApp client payment reminder -----------------------------------
  // Mirrors the vendor reminder pattern in AccountingVendors but pulls the
  // client name + phone + outstanding from the trip prop instead of a vendor
  // record. Only available when there's a real outstanding balance.
  const sendClientWhatsAppReminder = () => {
    const clientPhone = trip?.phone || trip?.customerPhone || trip?.traveller?.phone;
    if (!clientPhone) {
      toast.error('No client phone on file for this trip');
      return;
    }
    const clientName = trip?.guestName || trip?.customerName || trip?.traveller?.fullName || 'there';
    const destination = trip?.destination || 'your trip';
    const tripLabel = trip?.tripId || tripId;
    const departureDate = trip?.travelStartDate
      ? new Date(trip.travelStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '[departure_date]';
    const amount = `₹${Math.round(outstanding).toLocaleString('en-IN')}`;
    const agentName = 'Travcations Team';
    const msg = [
      `Hi ${clientName},`,
      '',
      `Friendly reminder — ${amount} is due for your ${destination} trip`,
      `(${tripLabel}) departing ${departureDate}.`,
      '',
      'Please transfer at your earliest convenience.',
      '',
      'Thanks,',
      `${agentName} — Travcations`,
    ].join('\n');
    const cleanPhone = String(clientPhone).replace(/[^0-9]/g, '');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-6 h-6 border-3 border-[#2C3E35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="trip-financials-tab">
      {/* WhatsApp (official) — send from this trip record */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setWaSendOpen(true)} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="trip-whatsapp-cloud-btn">
          <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
        </Button>
      </div>
      {waSendOpen && (
        <WhatsAppSendDialog open={waSendOpen} onOpenChange={setWaSendOpen} contextType="trip" contextId={tripId} title={trip?.guestName} />
      )}
      {/* Send Payment Link CTA */}
      {isFx ? (
        <Card className="p-5 bg-gradient-to-br from-indigo-50 via-white to-indigo-50/40 border-indigo-100" data-testid="fx-collection-card">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-indigo-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="text-sm font-bold text-slate-900">Foreign-currency quote — manual collection</h3>
              <p className="text-xs text-slate-600 mt-1">
                This quote is fixed at <span className="font-semibold">{fxCur} {fxPayable.toLocaleString()}</span>.
                Payment links are <strong>not auto-generated</strong> — the INR amount is only known once you enter that day's rate.
                Record each instalment below with the rate used; the INR is computed and stored per payment.
              </p>
              <p className="text-xs mt-2">
                Outstanding: <span className="font-mono font-semibold text-rose-700" data-testid="fx-outstanding">{fxCur} {fxOutstanding.toLocaleString()}</span>
                <span className="text-slate-400"> · Collected so far ₹{fmtNum(totalManualReceipts)}</span>
              </p>
            </div>
          </div>
        </Card>
      ) : (
      <Card className="p-5 bg-gradient-to-br from-teal-50 via-white to-emerald-50/40 border-teal-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Send className="h-4 w-4 text-teal-700" />
              Send Secure Payment Link
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {outstanding > 0
                ? <>Outstanding <span className="font-semibold text-slate-700">₹{fmtNum(outstanding)}</span> — share a branded payment link instantly via email or WhatsApp.</>
                : <>Generate a new branded payment link for this trip — shareable via email / WhatsApp.</>
              }
            </p>
          </div>
          <div className="flex gap-2">
            {outstanding > 0 && (
              <Button
                onClick={sendClientWhatsAppReminder}
                variant="outline"
                className="rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="whatsapp-client-reminder-btn"
                title="Send WhatsApp payment reminder to client"
              >
                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp Reminder
              </Button>
            )}
            <Button onClick={openSendDialog} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl shadow-md" data-testid="send-payment-link-btn">
              <Send className="h-4 w-4 mr-2" /> Send Payment Link
            </Button>
            <Button variant="outline" onClick={() => navigate('/payment-links')} className="rounded-xl" data-testid="view-all-links-btn">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
      )}

      <LivePaymentLinksCard links={paymentLinks} />

      {/* Payment Schedule — multiple instalment due dates per trip */}
      <Card className="overflow-hidden" data-testid="payment-schedule-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            Payment Schedule
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={addScheduleRow}
            data-testid="add-schedule-row-btn"
            className="text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Instalment
          </Button>
        </div>
        {scheduleDraft.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">
            No instalments scheduled. Click "+ Instalment" to add payment due dates.
          </div>
        ) : (
          <div className="px-5 py-3 space-y-2">
            {scheduleDraft.map((row, idx) => (
              <div
                key={idx}
                data-testid={`schedule-row-${idx}`}
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center"
              >
                <Input
                  className="col-span-4 h-9 text-sm"
                  value={row.label || ''}
                  onChange={(e) => updateScheduleRow(idx, 'label', e.target.value)}
                  placeholder="Label (e.g. Advance, Mid-payment)"
                  data-testid={`schedule-label-${idx}`}
                />
                <Input
                  type="number"
                  step="0.01"
                  className="col-span-3 h-9 text-sm font-mono"
                  value={row.amount || ''}
                  onChange={(e) => updateScheduleRow(idx, 'amount', e.target.value)}
                  placeholder="Amount"
                  data-testid={`schedule-amount-${idx}`}
                />
                <Input
                  type="date"
                  className="col-span-3 h-9 text-sm"
                  value={row.dueDate || ''}
                  onChange={(e) => updateScheduleRow(idx, 'dueDate', e.target.value)}
                  data-testid={`schedule-due-date-${idx}`}
                />
                <Select
                  value={row.status || 'due'}
                  onValueChange={(v) => updateScheduleRow(idx, 'status', v)}
                >
                  <SelectTrigger className="col-span-1 h-9 text-xs px-2" data-testid={`schedule-status-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="due">Due</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeScheduleRow(idx)}
                  data-testid={`remove-schedule-row-${idx}`}
                  className="col-span-1 h-9 w-9 text-rose-500 hover:text-rose-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            {scheduleDirty && (
              <div className="flex justify-end pt-2">
                <Button
                  size="sm"
                  onClick={persistSchedule}
                  disabled={savingSchedule}
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                  data-testid="save-schedule-btn"
                >
                  {savingSchedule ? 'Saving…' : 'Save Schedule'}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Manual receipts (Bank Transfer / UPI / Cash / Cheque) */}
      <Card className="overflow-hidden" data-testid="manual-receipts-card">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Banknote className="h-4 w-4 text-slate-400" />
            Amount Received (Manual Entries)
          </h3>
          <span className="text-xs text-slate-500">
            Total received: <span className="font-mono font-semibold text-emerald-700">₹{fmtNum(totalManualReceipts)}</span>
          </span>
        </div>
        <div className="px-5 py-3 border-b border-slate-50 grid grid-cols-2 sm:grid-cols-12 gap-2 items-end">
          <div className="col-span-2">
            <Label className="text-[10px]">Receipt Date</Label>
            <Input
              type="date"
              value={receiptForm.date}
              onChange={(e) => setReceiptForm((f) => ({ ...f, date: e.target.value }))}
              className="h-9 text-sm mt-1"
              data-testid="receipt-date-input"
            />
          </div>
          {isFx ? (
            <>
              <div className="col-span-2">
                <Label className="text-[10px]">Amount ({fxCur})</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={receiptForm.foreignAmount}
                  onChange={(e) => setReceiptForm((f) => ({ ...f, foreignAmount: e.target.value }))}
                  className="h-9 text-sm font-mono mt-1"
                  data-testid="receipt-foreign-amount-input"
                  placeholder="0"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px]">Rate (₹/{fxCur})</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={receiptForm.fxRate}
                  onChange={(e) => setReceiptForm((f) => ({ ...f, fxRate: e.target.value }))}
                  className="h-9 text-sm font-mono mt-1"
                  data-testid="receipt-fx-rate-input"
                  placeholder="86.50"
                />
              </div>
            </>
          ) : (
          <div className="col-span-2">
            <Label className="text-[10px]">Amount (₹)</Label>
            <Input
              type="number"
              step="0.01"
              value={receiptForm.amount}
              onChange={(e) => setReceiptForm((f) => ({ ...f, amount: e.target.value }))}
              className="h-9 text-sm font-mono mt-1"
              data-testid="receipt-amount-input"
              placeholder="0"
            />
          </div>
          )}
          <div className="col-span-2">
            <Label className="text-[10px]">Method</Label>
            <Select
              value={receiptForm.method}
              onValueChange={(v) => setReceiptForm((f) => ({ ...f, method: v }))}
            >
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="receipt-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px]">UTR / Ref No.</Label>
            <Input
              value={receiptForm.bankRef}
              onChange={(e) => setReceiptForm((f) => ({ ...f, bankRef: e.target.value }))}
              className="h-9 text-sm mt-1"
              data-testid="receipt-bank-ref-input"
              placeholder="HDFC123…"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-[10px]">Account (optional)</Label>
            <Select
              value={receiptForm.accountId || '__none__'}
              onValueChange={(v) => setReceiptForm((f) => ({ ...f, accountId: v === '__none__' ? '' : v }))}
            >
              <SelectTrigger className="h-9 text-sm mt-1" data-testid="receipt-account-select">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {bankAccounts.map((a) => (
                  <SelectItem key={a.accountId} value={a.accountId}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-[10px]">Note</Label>
            <Input
              value={receiptForm.note}
              onChange={(e) => setReceiptForm((f) => ({ ...f, note: e.target.value }))}
              className="h-9 text-sm mt-1"
              data-testid="receipt-note-input"
              placeholder="Advance, Mid-payment…"
            />
          </div>
          <div className="col-span-2">
            <Button
              onClick={addReceipt}
              disabled={savingReceipt}
              className="h-9 w-full bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="add-receipt-btn"
            >
              {savingReceipt ? 'Saving…' : (<><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Record</>)}
            </Button>
          </div>
          {isFx && (Number(receiptForm.foreignAmount) > 0 && Number(receiptForm.fxRate) > 0) && (
            <div className="col-span-2 sm:col-span-12 text-xs text-indigo-700" data-testid="receipt-fx-preview">
              Collected INR ≈ <span className="font-mono font-semibold">₹{fmtNum(Math.round(fxPreviewInr))}</span> = {fxCur} {Number(receiptForm.foreignAmount).toLocaleString()} × (rate {Number(receiptForm.fxRate)} + ₹{fxMarkup} markup). Stored immutably at this rate.
            </div>
          )}
        </div>
        {receipts.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">
            No manual receipts recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trip-receipts-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Method</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Ref</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Note</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {receipts.map((r) => (
                  <tr key={r.receiptId} data-testid={`receipt-row-${r.receiptId}`}>
                    <td className="px-4 py-2 text-xs text-slate-500">{r.date}</td>
                    <td className="px-4 py-2 text-xs text-slate-700 capitalize">
                      {(r.method || '').replace('_', ' ')}
                      {r.method === 'gateway' && r.gateway && (
                        <span className="block text-[10px] font-normal text-slate-400 capitalize">via {r.gateway}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs font-mono text-slate-500">{r.bankRef || '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{r.note || '—'}</td>
                    <td className="px-4 py-2 text-sm text-right font-mono font-semibold text-emerald-700">
                      {r.foreignCurrency && r.foreignAmount ? (
                        <span data-testid={`receipt-fx-cell-${r.receiptId}`}>
                          {r.foreignCurrency} {Number(r.foreignAmount).toLocaleString()}
                          <span className="block text-[10px] font-normal text-slate-400">@ {r.fxRate}+{r.fxMarkup} → ₹{fmtNum(r.amount)}</span>
                        </span>
                      ) : r.method === 'gateway' && r.gatewayFee > 0 ? (
                        <span data-testid={`receipt-gateway-cell-${r.receiptId}`}>
                          ₹{fmtNum(r.grossAmount || r.amount)}
                          <span className="block text-[10px] font-normal text-rose-500">Fee: ₹{fmtNum(r.gatewayFee)}</span>
                          <span className="block text-[10px] font-normal text-slate-400">Net: ₹{fmtNum(r.netAmount)}</span>
                        </span>
                      ) : (
                        <>₹{fmtNum(r.amount)}</>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteReceipt(r.receiptId)}
                        data-testid={`delete-receipt-${r.receiptId}`}
                        className="h-7 w-7 text-rose-400 hover:text-rose-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <MetricCard label="Trip Revenue" value={fmtNum(tripRevenue)} icon={TrendingUp} color="emerald" />
        <MetricCard label="Total Expenses" value={fmtNum(totalExpenseWithTax)} icon={TrendingDown} color="red" />
        <MetricCard label="Net Profit" value={fmtNum(profit)} icon={IndianRupee} color={profit >= 0 ? 'emerald' : 'red'} />
        <MetricCard label="Margin" value={`${margin}%`} icon={Percent} color="teal" isPercent />
        <MetricCard label="Collected" value={fmtNum(totalCollected)} icon={CreditCard} color="amber" />
      </div>

      {/* TCS / GST government-deposit tracking (Task 2 — Part 1) */}
      <TaxDepositCard tripId={tripId} />

      {/* Expense Breakdown */}
      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-slate-400" />
            Expense Breakdown
          </h3>
        </div>
        {expenses.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No expenses linked to this trip yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trip-expenses-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vendor</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Category</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Base</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">GST</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</th>
                  <th className="px-4 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {expenses.map(e => (
                  <tr key={e.expenseId}>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{e.date}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-700 font-medium">{e.vendorName || e.vendor || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{e.category}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-mono">₹{fmtNum(e.amount)}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-mono">₹{fmtNum(e.gstAmount)}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-mono font-semibold">₹{fmtNum(e.totalAmount || e.amount)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${statusColors[e.paymentStatus] || 'bg-slate-100 text-slate-600'}`}>
                        {e.paymentStatus || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr>
                  <td colSpan={3} className="px-4 py-2.5 text-sm font-semibold text-slate-700">Total</td>
                  <td className="px-4 py-2.5 text-sm text-right font-mono font-semibold">₹{fmtNum(totalExpenses)}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-mono font-semibold">₹{fmtNum(totalGSTPaid)}</td>
                  <td className="px-4 py-2.5 text-sm text-right font-mono font-bold">₹{fmtNum(totalExpenseWithTax)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Payment Transactions */}
      {payments.length > 0 && (
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-slate-400" />Payment Transactions ({payments.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="trip-payments-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Session</th>
                  <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Gateway</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
                  <th className="px-4 py-2 text-center text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</th>
                  <th className="px-4 py-2 text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {payments.map(p => (
                  <tr key={p.sessionId}>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-500">{p.sessionId?.slice(0, 20)}...</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 capitalize">{p.gateway}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-mono">₹{fmtNum(p.amount)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${statusColors[p.paymentStatus] || 'bg-slate-100 text-slate-600'}`}>
                        {p.paymentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-right text-slate-500">{p.createdAt?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Send Payment Link Dialog */}
      <SendPaymentLinkDialog
        open={showSend}
        onOpenChange={setShowSend}
        form={form}
        onFormChange={setForm}
        created={created}
        creating={creating}
        onGenerate={handleCreate}
        onCopy={copyShareUrl}
        onEmail={handleSendEmail}
        onClose={() => { setShowSend(false); setCreated(null); }}
        tripLabel={trip?.tripId || tripId}
      />
    </div>
  );
};

const CMAP = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  red: { bg: 'bg-red-100', text: 'text-red-600' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
};

const MetricCard = ({ label, value, icon: Icon, color, isPercent }) => {
  const c = CMAP[color] || CMAP.teal;
  return (
    <Card className="p-4" data-testid={`financial-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-9 h-9 ${c.bg} rounded-lg flex items-center justify-center mb-2`}>
        <Icon className={`h-4 w-4 ${c.text}`} />
      </div>
      <p className="text-xl font-bold text-slate-900">{isPercent ? value : `₹${value}`}</p>
      <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
    </Card>
  );
};

function fmtNum(n) { return (n || 0).toLocaleString('en-IN'); }

export default TripFinancials;
